import React, { useEffect } from "react";
import { PresenceType, useIdleTimer } from "react-idle-timer";
import { useTranslation } from "@scuf/localization";

import { ConfigurationVariables } from "../../config";
import { localeKeyMap } from "../../localization/keys";

import "./SessionTimer.scss";
import ConfirmationDialog from "../SharedComponents/ConfirmationDialogue";

const MILLISECONDS_PER_MINUTE = 60000;
const MILLISECONDS_PER_SECOND = 1000;

interface IProps {
  onAbsoluteTimeout: () => any;
  onIdleTimeout: () => any;
  onSessionExpiry?: () => void;
  sessionExpiryTime?: number;
}

/**
 * SessionTimer component displays prompts for idle and absolute session timeouts.
 * It triggers the provided callbacks when the timeouts occur.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onAbsoluteTimeout - Callback function to be called when absolute session timeout occurs.
 * @param {Function} props.onIdleTimeout - Callback function to be called when idle session timeout occurs.
 * @returns {JSX.Element} - The rendered SessionTimer component.
 */
const SessionTimer: React.FC<IProps> = ({
  onAbsoluteTimeout,
  onIdleTimeout,
  sessionExpiryTime, // ignore
  onSessionExpiry, // ignore
}: IProps): JSX.Element => {
  // const BUFFER_TIME_FOR_REFRESH = 10000;
  const [t] = useTranslation();
  const [isShowingIdlePrompt, setIsShowingIdlePrompt] = React.useState(false);

  const [lastActiveTimeForIdleTimeout, setLastActiveTimeForIdleTimeOut] =
    React.useState<number>(
      Number.parseInt(
        ConfigurationVariables.PROMPT_BEFORE_SESSION_IDLE_TIME_IN_MS
      )
    );

  const onIdleTimerPresenceChange = (presence: PresenceType) => {
    switch (presence?.type) {
      case "idle":
        onIdleTimeout();
        break;
      case "active":
        setIsShowingIdlePrompt(presence?.prompted);
        break;
    }
  };
  // const onAbsoluteTimerPresenceChange = (presence: PresenceType) => {
  //   switch (presence?.type) {
  //     case "idle":
  //       onAbsoluteTimeout();
  //       break;
  //     case "active":
  //       setIsShowingAbsolutePrompt(presence?.prompted);
  //       break;
  //   }
  // };
  // const onSessionExpiryPresenceChange = (presence: PresenceType) => {
  //   switch (presence?.type) {
  //     case "idle":
  //      // onSessionExpiry();
  //       sessionRefreshTimerReset();
  //       break;
  //   }
  // };

  // update the session expiry text in the toast

  useEffect(() => {
    // Starts a timer that updates the last active time every second.
    const intervalId = setInterval(() => {
      setLastActiveTimeForIdleTimeOut(getRemainingTimeForIdleTimeOut);
    }, MILLISECONDS_PER_SECOND);
    return () => clearInterval(intervalId);
  }, [isShowingIdlePrompt]);

  const formatTimeoutMessage = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / MILLISECONDS_PER_MINUTE);
    const seconds = Math.floor((milliseconds / MILLISECONDS_PER_SECOND) % 60);

    if (minutes > 0) {
      return t(localeKeyMap.sessionTimeoutMinutesAndSeconds, {
        minutes,
        seconds,
      });
    }
    return t(localeKeyMap.sessionTimeoutSeconds, { seconds });
  };

  // useEffect(() => {
  //   // Starts a timer that updates the last active time every second.
  //   const intervalId = setInterval(() => {
  //     setLastActiveTimeForAbsoluteTimeOut(getRemainingTimeForAbsoluteTimeOut);
  //   }, 1000);
  //   return () => clearInterval(intervalId);
  // }, [isShowingAbsolutePrompt]);

  // If the prompt before session expiry time is greater than the timeout,
  // set the prompt time to be 1 second less than the timeout.
  // useIdleTimer expects prompt time to be smaller than timeout
  // const promptBeforeTimeout = (timeout: number, promptBeforeIdle: number) => {
  //   if (promptBeforeIdle > timeout) {
  //     return timeout - 1000;
  //   } else {
  //     return promptBeforeIdle;
  //   }
  // };
  // const { getRemainingTime: getRemainingTimeForAbsoluteTimeOut } = useIdleTimer(
  //   {
  //     onPresenceChange: onAbsoluteTimerPresenceChange,
  //     timeout: Number.parseInt(
  //       ConfigurationVariables.ABSOLUTE_SESSION_TIME_IN_MS
  //     ),
  //     promptBeforeIdle: promptBeforeTimeout(
  //       Number.parseInt(ConfigurationVariables.ABSOLUTE_SESSION_TIME_IN_MS),
  //       Number.parseInt(
  //         ConfigurationVariables.PROMPT_BEFORE_SESSION_EXPIRY_TIME_IN_MS
  //       )
  //     ),
  //     events: [],
  //     eventsThrottle: 500,
  //     name: "absolute-timer",
  //   }
  // );
  const {
    reset: idleTimerReset,
    getRemainingTime: getRemainingTimeForIdleTimeOut,
  } = useIdleTimer({
    onPresenceChange: onIdleTimerPresenceChange,
    timeout: Number.parseInt(ConfigurationVariables.IDLE_SESSION_TIME_IN_MS),
    promptBeforeIdle: Number.parseInt(
      ConfigurationVariables.PROMPT_BEFORE_SESSION_IDLE_TIME_IN_MS
    ),
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
      "focus",
    ],
    eventsThrottle: 500,
    name: "idle-timer",
  });
  // const { reset: sessionRefreshTimerReset } = useIdleTimer({
  //   onPresenceChange: onSessionExpiryPresenceChange,
  //   timeout: sessionExpiryTime - BUFFER_TIME_FOR_REFRESH,
  //   events: [],
  //   eventsThrottle: 500,
  //   name: "session-expiry-timer",
  // });

  return (
    <>
      {isShowingIdlePrompt && (
        <ConfirmationDialog
          message={t(localeKeyMap.sessionTimeoutMessage, {
            time: formatTimeoutMessage(lastActiveTimeForIdleTimeout),
          })}
          title={t(localeKeyMap.sessionTimeoutTitle)}
          onModalClose={() => {
            idleTimerReset();
            setIsShowingIdlePrompt(false);
          }}
          primaryButtonText={t(localeKeyMap.resume)}
          onPrimaryButtonClick={() => {
            idleTimerReset();
            setIsShowingIdlePrompt(false);
          }}
        />
      )}
      {/* {isShowingAbsolutePrompt && (
        <ConfirmationDialog
          message={`To ensure the security of your account, your session will automatically logout in ${Math.floor(
            lastActiveTimeForAbsoluteTimeout / 1000
          )} seconds.`}
          title="Session Timeout"
          primaryButtonText="Logout"
          onModalClose={onAbsoluteTimeout}
          onPrimaryButtonClick={onAbsoluteTimeout}
        />
      )} */}
    </>
  );
};

export default SessionTimer;
